import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useStaticQuery, graphql } from "gatsby";
import { fontFaceMoontime, headStyle, center } from "./shared.module.scss";
import { getImage } from "gatsby-plugin-image";
import { validationRules, formatPhoneNumber, formatEmail } from '../utils/formValidation';
import { getEmailRecipient, getCCEmailRecipient, addEnvironmentInfo } from '../utils/emailRouting';

const ScheduleTourRHF = ({ toEmail = "", slug = null, sessionId = null, arrivalInfo = null }) => {
  const data = useStaticQuery(graphql`
    query {
      contentfulSpecialBlocks(blockTitle: { eq: "Schedule a Tour" }) {
        id
        blockText {
          blockText
        }
        blockImage {
          gatsbyImageData
        }
      }
    }
  `);

  const image = getImage(
    data.contentfulSpecialBlocks?.blockImage.gatsbyImageData,
  );
  const bgimg = image ? image.images.fallback.src : "";

  const [response, setResponse] = useState("");
  const [notification, setNotification] = useState("");
  const [mailpass, setMailpass] = useState("");
  const [mailuser, setMailuser] = useState("");
  const [sending, setSending] = useState(false);
  const [hasBeenSent, setHasBeenSent] = useState(false);

  // React Hook Form setup
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    watch,
    setValue,
    reset
  } = useForm({
    defaultValues: {
      fullname: "",
      email: "",
      phone: "",
      message: "",
      agreeToTextMessages: false
    },
    mode: "onChange"
  });

  // Watch phone and email fields for formatting
  const phoneValue = watch("phone");
  const emailValue = watch("email");

  // Format phone number on change
  useEffect(() => {
    if (phoneValue) {
      const formatted = formatPhoneNumber(phoneValue);
      if (formatted !== phoneValue) {
        setValue("phone", formatted);
      }
    }
  }, [phoneValue, setValue]);

  // Format email on change
  useEffect(() => {
    if (emailValue) {
      const formatted = formatEmail(emailValue);
      if (formatted !== emailValue) {
        setValue("email", formatted);
      }
    }
  }, [emailValue, setValue]);

  useEffect(() => {
    let mailpass = sessionStorage.getItem("mailpass");
    let mailuser = sessionStorage.getItem("mailuser");
    if (mailpass && mailpass.length > 0 && mailuser && mailuser.length > 0) {
      setMailpass(mailpass);
      setMailuser(mailuser);
    } else {
      getAuth(
        "https://mailut.mcklab.com/?action=login&username=orangepup&password=" +
          createPass(),
      );
    }
  }, []);

  const createPass = () => {
    let date = new Date();
    let dateString = date.toISOString().slice(0, 10);
    let pass = "froglegs";
    let encodedString = btoa(dateString + pass);
    return encodedString;
  };

  const getAuth = (url) => {
    fetch(url)
      .then((response) => response.json())
      .then((res) => {
        if (res.message_type == "success") {
          setMailpass(res.loggedin_pass);
          setMailuser(res.loggedin_user);
          sessionStorage.setItem("mailpass", res.loggedin_pass);
          sessionStorage.setItem("mailuser", res.loggedin_user);
        } else {
          setResponse(
            "We were not able to authorize this form to send emails. Please try again later",
          );
          setNotification("is-warning");
        }
      });
  };

  const sendMail = (url, data) => {
    fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "x-api-key": "tenOrangePups22",
      },
      body: JSON.stringify(data),
    })
      .then((response) => response.json())
      .then((res) => {
        console.log(res);
        if (res.message_type == "success" || res.errorCode == 200) {
          setResponse("Message sent successfully");
          setNotification("is-success");
          reset(); // Reset form on success
        } else {
          setResponse("Sorry, we were not able to send a message at this time");
          setNotification("is-danger");
        }
        setSending(false);
        setHasBeenSent(true);
      });
  };

  const onSubmit = (data) => {
    setSending(true);

    // Use environment-based email routing with UTM source
    const recipientEmail = getEmailRecipient(toEmail || "live@mckinley.com", slug, arrivalInfo?.utmSource);
    const ccEmail = getCCEmailRecipient(slug);

    // Prepare base email body
    const baseBody = `Full Name: ${data.fullname.trim()}
                      Email: ${data.email.trim()}
                      Phone: ${data.phone.replace(/\D/g, "")}
                      Notes: ${data.message.trim()}`;

    // Add environment and tracking info
    const fullBody = addEnvironmentInfo(baseBody, {
      utmSource: arrivalInfo?.utmSource,
      utmMedium: arrivalInfo?.utmMedium,
      utmCampaign: arrivalInfo?.utmCampaign,
      referrer: arrivalInfo?.referrer,
      sessionId: sessionId
    });

    let sendData = {
      loggedin_pass: mailpass,
      loggedin_user: mailuser,
      action: "mail",
      to: recipientEmail,
      cc: ccEmail,
      name: data.fullname.trim(),
      subject: "McKinley.com Schedule Tour Message from " + data.fullname.trim(),
      body: fullBody,
    };

    let endpoint = "https://mailut.mcklab.com";
    sendMail(endpoint, sendData);
  };

  return (
    <div
      id="stour"
      className=" container"
      style={{
        background: "url(" + bgimg + ")",
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      {response && (
        <div className={notification + " notification"}>{response}</div>
      )}
      <br />
      <div className="columns">
        <div className="column is-flex is-flex-direction-column is-justify-content-center">
          <h1 className={fontFaceMoontime + " " + headStyle + " " + center}>
            Contact Us
          </h1>
          <p style={{ padding: "2rem", color: "#fff", textAlign: "center" }}>
            {data.contentfulSpecialBlocks?.blockText.blockText}
          </p>
        </div>
        <div className="column " id="stour_contact">
          <form onSubmit={handleSubmit(onSubmit)}>
            {/* Full Name Field */}
            <div className="field">
              <label className="label" style={{ color: "#fff" }}>
                Full Name
              </label>
              <div className="control">
                <input
                  {...register("fullname", validationRules.fullName)}
                  className={`input ${errors.fullname ? 'is-danger' : ''}`}
                  type="text"
                  placeholder="Full Name: e.g Alex Smith"
                  style={{ maxWidth: "80%", padding: "2rem" }}
                />
              </div>
              {errors.fullname && (
                <p className="help is-danger">{errors.fullname.message}</p>
              )}
            </div>

            {/* Email Field */}
            <div className="field">
              <label className="label" style={{ color: "#fff" }}>
                Email
              </label>
              <div className="control">
                <input
                  {...register("email", validationRules.email)}
                  className={`input ${errors.email ? 'is-danger' : ''}`}
                  type="email"
                  placeholder="Email: e.g. alexsmith@gmail.com"
                  style={{ maxWidth: "80%", padding: "2rem" }}
                />
              </div>
              {errors.email && (
                <p className="help is-danger">{errors.email.message}</p>
              )}
            </div>

            {/* Phone Field */}
            <div className="field">
              <label className="label" style={{ color: "#fff" }}>
                Phone
              </label>
              <div className="control">
                <input
                  {...register("phone", validationRules.phone)}
                  className={`input ${errors.phone ? 'is-danger' : ''}`}
                  type="text"
                  placeholder="Phone: e.g 555-555-5555"
                  style={{ maxWidth: "80%", padding: "2rem" }}
                />
              </div>
              {errors.phone && (
                <p className="help is-danger">{errors.phone.message}</p>
              )}
            </div>

            {/* Message Field */}
            <div className="field">
              <label className="label" style={{ color: "#fff" }}>
                Message
              </label>
              <div className="control" style={{ maxWidth: "80%" }}>
                <textarea
                  {...register("message", validationRules.messageOptional)}
                  className={`textarea ${errors.message ? 'is-danger' : ''}`}
                  placeholder="Message"
                  style={{ maxWidth: "80%" }}
                ></textarea>
              </div>
              {errors.message && (
                <p className="help is-danger">{errors.message.message}</p>
              )}
            </div>

            {/* Consent Checkbox */}
            <div className="field">
              <div className="control">
                <label className="checkbox" style={{ color: "#fff" }}>
                  <input
                    {...register("agreeToTextMessages", validationRules.agreeToTextMessages)}
                    type="checkbox"
                    style={{ marginRight: "0.5rem" }}
                    className={errors.agreeToTextMessages ? 'is-danger' : ''}
                  />
                  &nbsp;By checking this box you agree to our Privacy Policy
                  <br />
                  and to receive text messages from McKinley. <br />
                  Reply STOP to opt out
                </label>
                {errors.agreeToTextMessages && (
                  <p className="help is-danger">{errors.agreeToTextMessages.message}</p>
                )}
              </div>
            </div>

            {/* Submit Button */}
            <div className="field">
              {!sending && !hasBeenSent && (
                <button
                  type="submit"
                  className="button is-link schedule-tour-button"
                  disabled={isSubmitting}
                  style={{
                    padding: "1.5rem",
                    backgroundColor: "#c7e9ed",
                    color: "#5C6369",
                  }}
                >
                  Submit
                </button>
              )}
              {sending && <p style={{color: "#fff"}}>Sending...</p>}
              {hasBeenSent && <p style={{color: "#fff"}}>Thank you!</p>}
            </div>
          </form>
        </div>
      </div>

      <br />
    </div>
  );
};

export default ScheduleTourRHF;